/**
 * Accessibility helper: when a user focuses a text input that the
 * on-screen keyboard would cover, scroll the input into the visible
 * portion of the viewport so they can see what they are typing.
 *
 * Behavior:
 *  - Only runs for real text-editable elements (input[type=text/email/
 *    password/search/url/tel/number], textarea, contenteditable).
 *  - Skips elements inside `.despia-chat-root` (the chat input is
 *    already pinned to ride above the keyboard via the existing
 *    `body.keyboard-open` CSS — scrolling would fight that).
 *  - Skips read-only / disabled inputs and non-text input types
 *    (checkbox, radio, color, file, button, etc.).
 *  - Uses `window.visualViewport` to know where the keyboard ends.
 *    On older browsers without visualViewport it falls back to
 *    `scrollIntoView({block: 'center'})`.
 *  - Scrolls the nearest scrollable ancestor (page-content, dialog
 *    body, ScrollArea viewport) so we don't break native-wrapper
 *    fixed-body layouts.
 *  - Smooth scroll, single-shot per focus. Re-runs once if the
 *    keyboard opens AFTER focus (visualViewport.resize fires).
 *  - Bails out cleanly on focusout / blur.
 */

const TEXT_INPUT_TYPES = new Set([
  'text', 'email', 'password', 'search', 'tel',
  'url', 'number', 'date', 'time', 'datetime-local', 'month', 'week',
]);
const KEYBOARD_SETTLE_MS = 320;
const TOP_MARGIN_PX = 12;
const BOTTOM_MARGIN_PX = 16;

let activeInput = null;
let pendingTimer = null;
let started = false;
// Popper wrappers we lifted via margin-top so we can restore them on blur.
const liftedPoppers = new Set();

function isTextEditable(el) {
  if (!el || el.disabled || el.readOnly) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'textarea') return true;
  if (tag === 'input') {
    const type = (el.type || 'text').toLowerCase();
    return TEXT_INPUT_TYPES.has(type);
  }
  return false;
}

function shouldAutoScroll(el) {
  if (!isTextEditable(el)) return false;
  // Chat input is already pinned above the keyboard via `.keyboard-open`
  // CSS. Don't double-handle.
  if (el.closest && el.closest('.despia-chat-root')) return false;
  // Opt-out hook for any future component that handles its own
  // keyboard offset.
  if (el.closest && el.closest('[data-keyboard-fixed="true"]')) return false;
  return true;
}

function findScrollContainer(el) {
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const cs = getComputedStyle(parent);
    const overflowY = cs.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Computes the bottom edge of the viewport that is NOT occluded by the
 * on-screen keyboard.
 *
 *  - iOS WKWebView / desktop / Chrome on Android with `adjustResize`:
 *    `visualViewport.height` shrinks when the keyboard opens. We trust
 *    it directly.
 *  - Android WebView (Despia and most embedded WebViews) often uses
 *    `windowSoftInputMode=adjustPan`, so `visualViewport.height` stays
 *    equal to `window.innerHeight` even when the keyboard is up.
 *    In that case we fall back to a heuristic: assume the keyboard
 *    occupies the bottom 45% of the screen (covers Gboard, Samsung
 *    Keyboard, SwiftKey at default heights). Over-estimating slightly
 *    is harmless — the popover just lifts a bit more than needed.
 */
function computeSafeBottom() {
  const vv = window.visualViewport;
  const innerH = window.innerHeight;
  if (vv && vv.height < innerH - 50) {
    return vv.offsetTop + vv.height;
  }
  const body = document.body;
  const isAndroidWrapper = body && (
    body.classList.contains('despia-android') ||
    body.classList.contains('android-native')
  );
  if (isAndroidWrapper) {
    // Assume keyboard takes ~45% of screen height.
    return innerH * 0.55;
  }
  return vv ? vv.offsetTop + vv.height : innerH;
}

function ensureVisible(el) {
  if (!el || !document.body.contains(el)) return;
  const vv = window.visualViewport;
  const rect = el.getBoundingClientRect();
  const visibleTop = vv ? vv.offsetTop : 0;
  const visibleBottom = computeSafeBottom();

  // Element bottom hidden under keyboard
  if (rect.bottom > visibleBottom - BOTTOM_MARGIN_PX) {
    const delta = rect.bottom - (visibleBottom - BOTTOM_MARGIN_PX);

    // Special case: inputs inside a Radix popper (Popover / DropdownMenu /
    // Combobox / etc.). The wrapper is `position: fixed` so there's no
    // scrollable ancestor to scroll. Lift the wrapper itself via
    // `margin-top` (Radix uses `transform` for positioning, so margin
    // does not clash with its layout calculation).
    const popperWrapper = el.closest('[data-radix-popper-content-wrapper]');
    if (popperWrapper) {
      const currentMargin = parseFloat(popperWrapper.style.marginTop || '0') || 0;
      // Compose: shift the wrapper UP by `delta` from its current offset.
      popperWrapper.style.transition = 'margin-top 220ms ease-out';
      popperWrapper.style.marginTop = `${currentMargin - delta}px`;
      liftedPoppers.add(popperWrapper);
      return;
    }

    const container = findScrollContainer(el);
    if (container) {
      container.scrollBy({ top: delta, behavior: 'smooth' });
    } else if ('scrollBehavior' in document.documentElement.style) {
      window.scrollBy({ top: delta, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ block: 'center' });
    }
    return;
  }
  // Element top hidden above the visible area (rare with keyboards but
  // can happen if a sticky header is opaque enough to occlude inputs).
  if (rect.top < visibleTop + TOP_MARGIN_PX) {
    const delta = (visibleTop + TOP_MARGIN_PX) - rect.top;
    const container = findScrollContainer(el);
    if (container) {
      container.scrollBy({ top: -delta, behavior: 'smooth' });
    } else if ('scrollBehavior' in document.documentElement.style) {
      window.scrollBy({ top: -delta, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ block: 'center' });
    }
  }
}

function restoreLiftedPoppers() {
  liftedPoppers.forEach((wrapper) => {
    if (!wrapper || !wrapper.isConnected) return;
    wrapper.style.transition = 'margin-top 200ms ease-out';
    wrapper.style.marginTop = '';
    // Clear the transition after it completes so subsequent Radix
    // positioning updates don't animate awkwardly.
    setTimeout(() => {
      if (wrapper && wrapper.isConnected) wrapper.style.transition = '';
    }, 240);
  });
  liftedPoppers.clear();
}

function schedule(el) {
  if (pendingTimer) clearTimeout(pendingTimer);
  // On Android Despia the keyboard appears without firing
  // visualViewport.resize, so we can't rely on a single 320ms check.
  // Run repeat checks at 120ms, 320ms, and 700ms — by 700ms even slow
  // Android keyboards have finished animating in.
  const runCheck = () => {
    if (activeInput && document.activeElement === activeInput) {
      ensureVisible(activeInput);
    }
  };
  setTimeout(runCheck, 120);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    runCheck();
    setTimeout(runCheck, 380);
  }, KEYBOARD_SETTLE_MS);
}

function onFocusIn(e) {
  const el = e.target;
  if (!shouldAutoScroll(el)) return;
  activeInput = el;
  // First pass: scroll after the keyboard has had time to open.
  schedule(el);
}

function onFocusOut() {
  activeInput = null;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  // Restore any popper wrappers we lifted above the keyboard. Delay
  // slightly so a fast focus-shift between two inputs in the same
  // popper doesn't trigger a restore-then-relift flicker.
  setTimeout(() => {
    if (!activeInput) restoreLiftedPoppers();
  }, 60);
}

function onViewportResize() {
  // Keyboard opened (or rotated) AFTER focus — re-check.
  if (activeInput && document.activeElement === activeInput) {
    // Small settle delay so the browser finishes laying out.
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      ensureVisible(activeInput);
    }, 80);
  }
}

export function initKeyboardScrollAssist() {
  if (started) return;
  started = true;
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onViewportResize);
  }
}
